import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Button, Badge, Spinner } from 'react-bootstrap';

import { redisFetch } from '../../../features/jobs/api/http';

// Taking a share of a drum that is ALREADY on a machine.
//
// A merge execution puts several products' shares on one bobbin, and until now
// it could only pair products launched TOGETHER: the merge registry lives for
// the length of one submit. The case on the floor is the other one — a CYYF 3x4
// lot of 8 300 m is half made when a CYYF 5x4 turns up, and the 5x4's blue,
// brown and green-yellow are the SAME conductor (Ø2.14, PVC, 4 mm²). Those
// metres come off the drums already on the machine; only the colours the 5x4
// does not share are drums that have to be made.
//
// So this row says "join that bobbin", and then the division is TYPED — every
// share on the drum, old and new. It has to be typed because a bobbin's total is
// not stored anywhere: it IS the sum of the shares, and with several products
// taking from one drum there is nothing to infer it from.
//
// Nothing here decides whether the division is legal. The server does, with the
// same rule it will use when it writes (`helpers/drumShares.js`), and this shows
// the answer as the planner types: a drum already made must divide into exactly
// what it holds, one not yet made is re-planned to the sum, and the drum on the
// machine is refused until it comes off.

const fmt = n => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('ro-RO') : '—');

const COLOR_HEX = {
  BLUE: '#2f7cff',
  BROWN: '#8b5a2b',
  BLACK: '#222222',
  GREY: '#7a7a7a',
  GRAY: '#7a7a7a',
  'GREEN-YELLOW': '#9acd32',
  GREEN: '#2e8b57',
  YELLOW: '#f0c400',
  RED: '#d9534f',
  WHITE: '#e5e5e5',
  ORANGE: '#f39c12'
};

const Dot = ({ color }) => {
  const hex = COLOR_HEX[String(color || '').toUpperCase()];
  if (!hex) return null;
  return (
    <span
      title={color}
      style={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: hex,
        border: '1px solid rgba(255,255,255,0.25)',
        display: 'inline-block',
        flex: '0 0 auto'
      }}
    />
  );
};

/**
 * @param phase         the phase of this execution
 * @param color         its colour — a share must be the same conductor
 * @param wireDiameter  its wire Ø, where the phase has one
 * @param share         what THIS product takes (the execution's own length)
 * @param productLabel  for the division table
 * @param value         `exec.mergeOnto` as it stands
 * @param onChange      (mergeOnto | null) => void
 * @param shareEdits    the donor shares typed so far, for the whole launch
 * @param onShareEdits  (next) => void
 */
const ShareOntoDrumPicker = ({
  phase,
  color,
  wireDiameter,
  share,
  productLabel = 'this product',
  value,
  onChange,
  shareEdits = [],
  onShareEdits,
  lotEdits = [],
  onLotEdits
}) => {
  const [all, setAll] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(null);
  const [showOthers, setShowOthers] = useState(false);

  const chosen = String(value?.tokenId || '');

  // Every queued drum of this PHASE, and the colour/Ø narrowing is done here.
  //
  // Asking the server for the narrow set was how this came out empty with no way
  // to tell why: a phase with nothing queued and a colour with nothing queued
  // look identical from the outside, and the planner is left looking at a
  // dropdown with one option and no explanation. With the whole phase in hand
  // the row can say which of the two it is — and offer the rest.
  useEffect(() => {
    if (!phase) return;
    let alive = true;
    setLoading(true);
    setErr('');

    redisFetch(`/api/redis/jobs/shareable-drums?phase=${encodeURIComponent(phase)}`)
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        if (!d?.success) throw new Error(d?.error || 'could not read the queues');
        setAll(Array.isArray(d.drums) ? d.drums : []);
      })
      .catch(e => alive && setErr(String(e.message || e)))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [phase]);

  // A share means those metres come off THAT bobbin, so the same conductor:
  // same colour, and the same wire where both are known.
  const sameConductor = useCallback(
    d => {
      if (color && String(d.color || '').toUpperCase() !== String(color).toUpperCase()) {
        return false;
      }
      const w = Number(wireDiameter);
      const dw = Number(d.wireDiameter);
      if (Number.isFinite(w) && Number.isFinite(dw) && Math.abs(w - dw) > 0.0005) return false;
      return true;
    },
    [color, wireDiameter]
  );

  const matching = useMemo(() => (all || []).filter(sameConductor), [all, sameConductor]);
  const others = useMemo(() => (all || []).filter(d => !sameConductor(d)), [all, sameConductor]);
  const drums = showOthers ? [...matching, ...others] : matching;

  const drum = useMemo(
    () => (all || []).find(d => d.tokenId === chosen) || null,
    [all, chosen]
  );

  // The division, checked by the server as it is typed.
  useEffect(() => {
    if (!drum) {
      setPreview(null);
      return;
    }
    let alive = true;
    const edit = shareEdits.find(
      e => e.tokenId === drum.tokenId && String(e.jobId) === String(drum.jobId)
    );

    redisFetch('/api/redis/jobs/merge-onto-preview', {
      method: 'POST',
      body: JSON.stringify({
        bobbins: [
          {
            tokenId: drum.tokenId,
            ownerJobIds: [drum.jobId],
            owners: edit ? [{ jobId: drum.jobId, taskId: drum.taskId, share: edit.share }] : [],
            incoming: [{ productLabel, share }]
          }
        ]
      })
    })
      .then(r => r.json())
      .then(d => alive && setPreview(d?.bobbins?.[0] || null))
      .catch(() => alive && setPreview(null));

    return () => {
      alive = false;
    };
  }, [drum, share, shareEdits, productLabel]);

  const donorShare = useMemo(() => {
    if (!drum) return '';
    const edit = shareEdits.find(
      e => e.tokenId === drum.tokenId && String(e.jobId) === String(drum.jobId)
    );
    return edit ? edit.share : drum.plannedLength;
  }, [drum, shareEdits]);

  // `forDrum` is passed explicitly when the drum is being CHOSEN: `drum` comes
  // from the `value` prop, and at that moment `onChange` has been called but the
  // prop has not come back yet — so a setter that read `drum` would find null
  // and drop the default without a word. That is exactly what left the donor's
  // share at its old 8 300 and made every division read 5 000 m too long.
  const setDonorShare = (next, forDrum = drum) => {
    if (!forDrum || !onShareEdits) return;
    const rest = shareEdits.filter(
      e => !(e.tokenId === forDrum.tokenId && String(e.jobId) === String(forDrum.jobId))
    );
    onShareEdits([
      ...rest,
      { jobId: forDrum.jobId, taskId: forDrum.taskId, tokenId: forDrum.tokenId, share: next }
    ]);
  };

  // The donor's LOT — a length of CABLE, so it is typed and never derived from
  // the drum shares: 3 300 m of a 3-core cable needs 3 300 m of each of three
  // drums, and subtracting the share once per drum would take an 8 300 m lot to
  // zero. One value per product, however many of its drums are shared.
  const donorLot = useMemo(() => {
    if (!drum) return '';
    const edit = lotEdits.find(e => String(e.jobId) === String(drum.jobId));
    return edit ? edit.lot : drum.lot;
  }, [drum, lotEdits]);

  const setDonorLot = (next, forDrum = drum) => {
    if (!forDrum || !onLotEdits) return;
    onLotEdits([
      ...lotEdits.filter(e => String(e.jobId) !== String(forDrum.jobId)),
      { jobId: forDrum.jobId, lot: next }
    ]);
  };

  const pick = tokenId => {
    const d = (drums || []).find(x => x.tokenId === tokenId);
    if (!d) {
      onChange?.(null);
      return;
    }
    onChange?.({ tokenId: d.tokenId, jobId: d.jobId, taskId: d.taskId });

    // Opened on a division that ALREADY ADDS UP, so the planner starts from
    // something valid and types over it rather than starting from an error.
    //
    // The two defaults are different numbers on purpose, and the drum that
    // over-ran shows why. K-4a5e8d99 was planned for 8 300 m and came off
    // holding 8 100.252:
    //
    //   the SHARE  must match the BOBBIN — 8 100.252 − 5 000 = 3 100.252, or the
    //              shares promise metres that are not on the drum
    //   the LOT    is a PLAN — 8 300 − 5 000 = 3 300, the length of cable this
    //              product is now going to make
    //
    // Defaulting both to the same figure is how one of them comes out wrong.
    const made = ['DONE', 'COMPLETED', 'FINISHED'].includes(String(d.status || '').toUpperCase());
    const holds = made ? Number(d.producedLength) || 0 : Number(d.plannedLength) || 0;
    const takes = Number(share) || 0;

    setDonorShare(Math.max(0, Number((holds - takes).toFixed(3))), d);

    if (!lotEdits.some(e => String(e.jobId) === String(d.jobId))) {
      const planned = Number(d.plannedLength) || 0;
      setDonorLot(Math.max(0, Number((planned - takes).toFixed(3))), d);
    }
  };

  return (
    <Form.Group>
      <Form.Label>
        Share an existing drum{' '}
        <span className="text-700 fs-10">— take these metres off a bobbin already queued</span>
      </Form.Label>

      <Form.Select
        size="sm"
        value={chosen}
        disabled={loading}
        onChange={e => pick(e.target.value)}
      >
        <option value="">No — make a new drum</option>
        {drums.map(d => (
          <option key={d.tokenId} value={d.tokenId} disabled={!d.shareable}>
            {d.productLabel} · lot {fmt(d.lot)} m · {d.tokenId} · {fmt(d.plannedLength)} m ·{' '}
            {d.status}
            {d.machineId ? ` · ${String(d.machineId).toUpperCase()}` : ''}
            {sameConductor(d) ? '' : ` · ${d.color || 'no colour'}`}
            {d.onMachineNow ? ' · ON THE MACHINE NOW' : ''}
            {d.shareable ? '' : ` — ${d.reason}`}
          </option>
        ))}
      </Form.Select>

      {loading && (
        <div className="fs-10 text-700 mt-1">
          <Spinner animation="border" size="sm" className="me-1" />
          reading the queues…
        </div>
      )}
      {err && <div className="fs-10 text-danger mt-1">{err}</div>}

      {/* An empty list has to say WHICH kind of empty it is. "Nothing of this
          phase is queued" and "nothing of this colour is queued" look identical
          in a dropdown with one option, and the planner cannot tell which
          without being told. */}
      {!loading && !err && all && !matching.length && (
        <div className="fs-10 text-700 mt-1">
          Looking for <b>{phase}</b>
          {color ? (
            <>
              {' '}
              · <b>{color}</b>
            </>
          ) : null}
          {Number.isFinite(Number(wireDiameter)) ? ` · Ø${Number(wireDiameter).toFixed(2)}` : ''}.{' '}
          {!all.length ? (
            <>Nothing of this phase is queued on any machine — this will be a new drum.</>
          ) : (
            <>
              {all.length} {all.length === 1 ? 'drum' : 'drums'} of this phase are queued, none of
              them this conductor.{' '}
              <Button
                variant="link"
                className="p-0 fs-10 align-baseline"
                onClick={() => setShowOthers(true)}
              >
                show them anyway
              </Button>
            </>
          )}
        </div>
      )}

      {!loading && !err && showOthers && others.length > 0 && (
        <div className="fs-10 text-warning mt-1">
          The ones added are a different conductor — taking a share of one means
          these metres come off THAT bobbin, so check the colour and the wire
          before choosing.
        </div>
      )}

      {drum && (
        <div className="mt-2 p-2 border border-200 rounded">
          <div className="d-flex align-items-center gap-2 fs-10 mb-2">
            <Dot color={drum.color} />
            <span className="font-monospace">{drum.tokenId}</span>
            <Badge variant="phoenix" bg={drum.status === 'DONE' ? 'primary' : 'warning'} className="fs-10">
              {drum.status}
            </Badge>
            <span className="text-700">
              holds {fmt(drum.producedLength)} m of {fmt(drum.plannedLength)} m
              {drum.machineId ? ` · ${String(drum.machineId).toUpperCase()}` : ''}
            </span>
          </div>

          {/* Every share on the bobbin, typed. With N products on one drum there
              is no single number to infer the rest from. */}
          <div className="d-flex align-items-center gap-2 mb-1">
            <span className="fs-10" style={{ minWidth: 150 }}>
              {drum.productLabel} keeps
            </span>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              style={{ width: 110 }}
              value={donorShare}
              onChange={e => setDonorShare(e.target.value)}
            />
            <span className="fs-10 text-700">m (was {fmt(drum.plannedLength)})</span>
          </div>

          <div className="d-flex align-items-center gap-2 mb-1">
            <span className="fs-10" style={{ minWidth: 150 }}>
              {productLabel} takes
            </span>
            <span className="font-monospace fs-9 fw-bold">{fmt(share)} m</span>
            <span className="fs-10 text-700">— this row&apos;s own length</span>
          </div>

          {/* The LOT of the product giving the share up. A length of cable, not
              a sum of drums, so it is typed: one value per product however many
              of its drums are shared. */}
          <div className="d-flex align-items-center gap-2">
            <span className="fs-10" style={{ minWidth: 150 }}>
              {drum.productLabel} lot becomes
            </span>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              style={{ width: 110 }}
              value={donorLot}
              onChange={e => setDonorLot(e.target.value)}
            />
            <span className="fs-10 text-700">m (was {fmt(drum.lot)})</span>
          </div>

          {/* The drum that is turning. The mechanism is the worker's own
              same-token rollover: it closes this product's share at exactly the
              length typed above, moves the counter baseline on by that much, and
              carries anything past it onto the next share — so the counter never
              stops and the change happens on it. Worth saying, because a mistake
              here lands on a bobbin already in motion. */}
          {drum.onMachineNow && (
            <div className="fs-10 text-warning mt-1">
              <b>This drum is turning right now.</b> The change happens live: at{' '}
              {fmt(donorShare)} m the counter rolls over to the next share and keeps
              going.
              {Number(drum.producedLength) > Number(donorShare) && (
                <>
                  {' '}
                  The counter is already past it — the rollover will happen on the
                  next tick, and the{' '}
                  {fmt(Number(drum.producedLength) - Number(donorShare))} m already
                  wound beyond it are carried onto {productLabel}.
                </>
              )}
            </div>
          )}

          {preview && (
            <div className="fs-10 mt-2">
              <div>
                shares add up to <b>{fmt(preview.total)} m</b> against{' '}
                {preview.finished ? 'what the drum holds' : 'its plan'}{' '}
                <b>{fmt(preview.reference)} m</b>
                {preview.delta !== 0 && (
                  <>
                    {' '}
                    ·{' '}
                    <span className={preview.finished ? 'text-danger' : 'text-warning'}>
                      {preview.delta > 0 ? '+' : ''}
                      {fmt(preview.delta)} m
                    </span>
                  </>
                )}
              </div>
              {(preview.errors || []).map((e, i) => (
                <div key={i} className="text-danger">
                  ✖ {e}
                </div>
              ))}
              {(preview.warnings || []).map((w, i) => (
                <div key={i} className="text-warning">
                  ⚠ {w}
                </div>
              ))}
              {!preview.errors?.length && !preview.warnings?.length && (
                <div className="text-success">✓ divides cleanly — no new drum, no queue change</div>
              )}
            </div>
          )}

          <Button
            variant="link"
            size="sm"
            className="p-0 fs-10 mt-1"
            onClick={() => {
              onChange?.(null);
              if (onShareEdits) {
                onShareEdits(
                  shareEdits.filter(
                    e => !(e.tokenId === drum.tokenId && String(e.jobId) === String(drum.jobId))
                  )
                );
              }
            }}
          >
            make a new drum instead
          </Button>
        </div>
      )}
    </Form.Group>
  );
};

export default ShareOntoDrumPicker;
